import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { EmailLogsParams, emailLogService, EmailLogStatus } from '@/services/emailLog.service';

export const useEmailLogs = (params: EmailLogsParams, enabled = true) =>
  useQuery({
    queryKey: ['email-logs', params],
    queryFn: () => emailLogService.list(params),
    placeholderData: keepPreviousData,
    enabled,
  });

// Provider-accepted statuses: a log in any of these means the email left our
// side successfully (Mandrill echoes 'sent'/'queued'; a webhook may later
// promote to 'delivered'). 'pending'/'failed'/'bounced'/'complained' do not
// count as a successful send.
const SENT_STATUSES = new Set<EmailLogStatus>(['sent', 'queued', 'delivered']);

// Whether a wizard step's email has ALREADY been sent for this lead in a prior
// session - the persisted source of truth behind the "Update and Proceed" /
// "Client Onboarded" gates, so they stay enabled after a refresh or on reopen
// instead of relying on ephemeral in-session send state (Fix 3).
export const useLeadEmailSent = (
  leadId: number,
  slugs: string[],
  enabled = true,
  // When true, a send only counts if it also minted a reference number. The
  // quotation gate needs this: the wizard may not advance to the Payment Plan
  // until a quotation actually went out AND carries the AK/QT/... reference the
  // rest of the flow (payment confirmation, billing handover, PDF) quotes.
  requireReferenceNumber = false,
) => {
  // Server-side filter to just this step's template slugs, so the check is
  // precise regardless of how many other emails the lead has (no fixed
  // most-recent-N window). The returned rows are only these templates' sends.
  const query = useEmailLogs(
    { leadId, templateSlug: slugs.join(','), limit: 20 },
    enabled && Number.isFinite(leadId) && leadId > 0 && slugs.length > 0,
  );
  const sentRows = (query.data?.rows ?? []).filter((log) => SENT_STATUSES.has(log.status));
  const alreadySent = requireReferenceNumber
    ? sentRows.some((log) => Boolean(log.referenceNumber))
    : sentRows.length > 0;
  // The reference of the most recent qualifying send, for the "already sent" hint.
  const referenceNumber =
    sentRows.find((log) => Boolean(log.referenceNumber))?.referenceNumber ?? null;
  return { alreadySent, referenceNumber, isLoading: query.isLoading };
};
